import React, { useCallback, useEffect, useState } from 'react';
import {
  Download,
  Eye,
  File,
  FileText,
  Grid,
  Image,
  List,
  MoreVertical,
  Plus,
  Search,
  Trash2,
  Upload
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { documentService } from '../../services/documentService';
import DocumentUpload from './DocumentUpload';

const DocumentList = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('updatedAt');
  const [viewMode, setViewMode] = useState('grid');
  const [showUpload, setShowUpload] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [showActions, setShowActions] = useState(null);

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const docs = await documentService.getUserDocuments(user.uid);
      setDocuments(docs);
    } catch (error) {
      toast.error('Error loading documents');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const deleteDocument = async (doc) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await documentService.deleteDocument(doc.id, doc.path, user.uid);
        toast.success('Document deleted successfully');
        loadDocuments();
      } catch (error) {
        toast.error('Error deleting document');
        console.error(error);
      }
    }
  };

  const deleteSelectedDocuments = async () => {
    if (selectedDocs.size === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedDocs.size} document(s)?`)) {
      try {
        const deletePromises = Array.from(selectedDocs).map((docId) => {
          const doc = documents.find((item) => item.id === docId);
          return documentService.deleteDocument(doc.id, doc.path, user.uid);
        });

        await Promise.all(deletePromises);
        toast.success(`${selectedDocs.size} document(s) deleted successfully`);
        setSelectedDocs(new Set());
        loadDocuments();
      } catch (error) {
        toast.error('Error deleting documents');
        console.error(error);
      }
    }
  };

  const toggleDocumentSelection = (docId) => {
    const nextSelection = new Set(selectedDocs);
    if (nextSelection.has(docId)) {
      nextSelection.delete(docId);
    } else {
      nextSelection.add(docId);
    }
    setSelectedDocs(nextSelection);
  };

  const filteredDocuments = documents
    .filter((doc) =>
      doc.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (filterType === 'all' || doc.type.includes(filterType))
    )
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'size') return b.size - a.size;
      return new Date(b[sortBy]?.toDate?.() || b[sortBy]) - new Date(a[sortBy]?.toDate?.() || a[sortBy]);
    });

  const selectAllDocuments = () => {
    if (selectedDocs.size === filteredDocuments.length) {
      setSelectedDocs(new Set());
      return;
    }
    setSelectedDocs(new Set(filteredDocuments.map((doc) => doc.id)));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getFileIcon = (type, size = 'h-6 w-6') => {
    if (type.includes('pdf')) return <FileText className={`${size} text-rose-500`} />;
    if (type.includes('word')) return <FileText className={`${size} text-blue-500`} />;
    if (type.includes('excel') || type.includes('sheet')) return <FileText className={`${size} text-emerald-500`} />;
    if (type.includes('powerpoint') || type.includes('presentation')) return <FileText className={`${size} text-amber-500`} />;
    if (type.includes('image')) return <Image className={`${size} text-fuchsia-500`} />;
    return <File className={`${size} text-slate-400`} />;
  };

  const getFileTypeLabel = (type) => {
    if (type.includes('pdf')) return 'PDF';
    if (type.includes('word')) return 'Word';
    if (type.includes('excel') || type.includes('sheet')) return 'Excel';
    if (type.includes('powerpoint') || type.includes('presentation')) return 'PowerPoint';
    if (type.includes('image')) return 'Image';
    return type.split('/')[1]?.toUpperCase() || 'File';
  };

  const getFileTypeBadgeClass = (type) => {
    if (type.includes('pdf')) return 'bg-rose-50 text-rose-700 ring-1 ring-rose-100';
    if (type.includes('word')) return 'bg-blue-50 text-blue-700 ring-1 ring-blue-100';
    if (type.includes('excel') || type.includes('sheet')) return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100';
    if (type.includes('powerpoint') || type.includes('presentation')) return 'bg-amber-50 text-amber-700 ring-1 ring-amber-100';
    if (type.includes('image')) return 'bg-fuchsia-50 text-fuchsia-700 ring-1 ring-fuchsia-100';
    return 'bg-slate-100 text-slate-600 ring-1 ring-slate-200';
  };

  if (showUpload) {
    return (
      <DocumentUpload
        onUploadComplete={() => {
          setShowUpload(false);
          loadDocuments();
        }}
        onClose={() => setShowUpload(false)}
      />
    );
  }

  return (
    <div className="space-y-8">
      <header className="rounded-[28px] border border-slate-200/80 bg-gradient-to-br from-white via-white to-blue-50/70 px-6 py-8 shadow-sm sm:px-8">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
              <Upload className="h-3.5 w-3.5" />
              Docs Library
            </div>
            <div className="space-y-2">
              <h1 className="text-4xl font-bold tracking-tight text-slate-900">My Documents</h1>
              <p className="max-w-3xl text-base text-slate-500">
                {documents.length} document{documents.length !== 1 ? 's' : ''} • {formatFileSize(documents.reduce((total, doc) => total + (doc.size || 0), 0))} total
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Upload Documents
          </button>
        </div>
      </header>

      {selectedDocs.size > 0 && (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 px-5 py-4 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm font-semibold text-blue-900">
              {selectedDocs.size} document{selectedDocs.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={deleteSelectedDocuments}
                className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
              >
                <Trash2 className="h-4 w-4" />
                Delete Selected
              </button>
              <button
                onClick={() => setSelectedDocs(new Set())}
                className="rounded-xl border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-blue-300 focus:bg-white"
            />
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 focus:border-blue-300 focus:ring-0"
            >
              <option value="all">All Types</option>
              <option value="pdf">PDF</option>
              <option value="word">Word</option>
              <option value="excel">Excel</option>
              <option value="powerpoint">PowerPoint</option>
              <option value="image">Images</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 focus:border-blue-300 focus:ring-0"
            >
              <option value="updatedAt">Last Modified</option>
              <option value="createdAt">Date Created</option>
              <option value="name">Name</option>
              <option value="size">Size</option>
            </select>

            <div className="inline-flex overflow-hidden rounded-xl border border-slate-200 bg-white">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-3 transition-colors ${viewMode === 'grid' ? 'bg-blue-50 text-blue-700' : 'text-slate-500 hover:bg-slate-50'}`}
              >
                <Grid className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-3 transition-colors ${viewMode === 'list' ? 'bg-blue-50 text-blue-700' : 'text-slate-500 hover:bg-slate-50'}`}
              >
                <List className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-[20px] border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <div className="px-8 py-12 text-center">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
            <p className="mt-4 text-sm text-slate-500">Loading documents...</p>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="px-8 py-14 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <FileText className="h-7 w-7" />
            </div>
            <h2 className="mt-5 text-2xl font-semibold text-slate-900">
              {searchTerm ? 'No matching documents' : 'No documents uploaded yet'}
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500">
              {searchTerm ? 'Try broadening your search or changing the filter.' : 'Upload your first document to start organizing your files here.'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowUpload(true)}
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" />
                Upload Your First Document
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="p-6">
            <div className="mb-5 border-b border-slate-200 pb-4">
              <label className="inline-flex items-center gap-3 text-sm font-medium text-slate-600">
                <input
                  type="checkbox"
                  checked={selectedDocs.size === filteredDocuments.length}
                  onChange={selectAllDocuments}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                Select all documents
              </label>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {filteredDocuments.map((doc) => (
                <article
                  key={doc.id}
                  className="group relative flex flex-col justify-between rounded-[20px] border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="space-y-5">
                    <div className="flex items-start justify-between gap-4">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedDocs.has(doc.id)}
                          onChange={() => toggleDocumentSelection(doc.id)}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                      </label>
                      <div className="relative z-20">
                        <button
                          onClick={() => setShowActions(showActions === doc.id ? null : doc.id)}
                          className="rounded-lg p-2 text-slate-400 opacity-0 transition hover:bg-slate-100 hover:text-slate-600 group-hover:opacity-100"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>

                        {showActions === doc.id && (
                          <div className="absolute right-0 top-full mt-2 w-44 rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                            <Link
                              to={`/document/${doc.id}`}
                              onClick={() => setShowActions(null)}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                            >
                              <Eye className="h-4 w-4" />
                              View
                            </Link>
                            <a
                              href={doc.url}
                              download={doc.name}
                              onClick={() => setShowActions(null)}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                            >
                              <Download className="h-4 w-4" />
                              Download
                            </a>
                            <button
                              onClick={() => {
                                setShowActions(null);
                                deleteDocument(doc);
                              }}
                              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-rose-600 hover:bg-rose-50"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-50">
                        {getFileIcon(doc.type, 'h-8 w-8')}
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] ${getFileTypeBadgeClass(doc.type)}`}>
                        {getFileTypeLabel(doc.type)}
                      </span>
                    </div>

                    <div className="space-y-1">
                      <h2 className="text-lg font-bold text-slate-900" title={doc.name}>
                        {doc.name}
                      </h2>
                      <p className="text-sm font-medium text-slate-500">
                        {formatFileSize(doc.size)} • Updated {formatDate(doc.updatedAt)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-wrap gap-3">
                    <Link
                      to={`/document/${doc.id}`}
                      className="rounded-xl bg-blue-50 px-5 py-2.5 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100"
                    >
                      Open
                    </Link>
                    <a
                      href={doc.url}
                      download={doc.name}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </a>
                    <button
                      onClick={() => deleteDocument(doc)}
                      className="ml-auto inline-flex items-center gap-2 rounded-xl border border-rose-100 px-5 py-2.5 text-sm font-semibold text-rose-500 transition-colors hover:bg-rose-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectedDocs.size === filteredDocuments.length && filteredDocuments.length > 0}
                      onChange={selectAllDocuments}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Type</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Size</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Modified</th>
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {filteredDocuments.map((doc) => (
                  <tr key={doc.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedDocs.has(doc.id)}
                        onChange={() => toggleDocumentSelection(doc.id)}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50">
                          {getFileIcon(doc.type, 'h-5 w-5')}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-slate-900">{doc.name}</div>
                          {doc.description && <div className="text-sm text-slate-500">{doc.description}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getFileTypeBadgeClass(doc.type)}`}>
                        {getFileTypeLabel(doc.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700">{formatFileSize(doc.size)}</td>
                    <td className="px-6 py-4 text-sm text-slate-500">{formatDate(doc.updatedAt)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/document/${doc.id}`}
                          className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"
                          title="View Document"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        <a
                          href={doc.url}
                          download={doc.name}
                          className="rounded-lg p-2 text-emerald-600 hover:bg-emerald-50"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                        <button
                          onClick={() => deleteDocument(doc)}
                          className="rounded-lg p-2 text-rose-600 hover:bg-rose-50"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showActions && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setShowActions(null)}
        />
      )}
    </div>
  );
};

export default DocumentList;
